
import { useEffect, useRef } from 'react'

import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'

import { addToast } from 'components/Toast/Toast'
import {
    useSetAccessToken,
    useSetUserProfile
} from 'store'
import { useLocalStorageSetAccessToken } from 'utils'

type TwitterLoginResult =
    | {
          requires_two_factor: true
          challenge: string
          two_factor_auth_type: 'qr_authenticator' | 'sms'
      }
    | {
          requires_two_factor?: false
          token: string
          userProfile: any
      }

type EarlyCallbackResult =
    | { ok: true; data: TwitterLoginResult }
    | { ok: false; error: string }

declare global {
    interface Window {
        __twitterCallbackEarly?: {
            startedAt: number
            promise: Promise<EarlyCallbackResult>
        }
    }
}

export const TwitterLogin = () => {
    const navigate = useNavigate()
    const handledCallback = useRef(false)

    useEffect(() => {
        document.getElementById('twitter-initial-loader')?.remove()
    }, [])

    useEffect(() => {
        if (handledCallback.current) return

        handledCallback.current = true

        const cleanup = () => {
            sessionStorage.removeItem('twitter_login_nonce')
            sessionStorage.removeItem('twitter_login_started_at')
            window.__twitterCallbackEarly = undefined
        }

        const handleCallback = async () => {
            try {
                const early = window.__twitterCallbackEarly

                if (!early) {
                    throw new Error('Twitter callback request is missing')
                }

                const result = await early.promise

                if (!result.ok) {
                    throw new Error(result.error)
                }

                const data = result.data

                if (data.requires_two_factor === true) {
                    if (
                        !data.challenge ||
                        !['qr_authenticator', 'sms'].includes(
                            data.two_factor_auth_type
                        )
                    ) {
                        throw new Error('Invalid 2FA challenge')
                    }

                    sessionStorage.setItem(
                        'pending_social_2fa',
                        JSON.stringify({
                            challenge: data.challenge,
                            two_factor_auth_type: data.two_factor_auth_type,
                            created_at: Date.now()
                        })
                    )

                    navigate('/', { replace: true })
                    return
                }

                const { token, userProfile } = data

                if (!token || !userProfile) {
                    throw new Error('Invalid Twitter login response')
                }

                useSetUserProfile(userProfile)
                useSetAccessToken(token)
                useLocalStorageSetAccessToken(token)

                navigate('/', { replace: true })
            } catch (error) {
                console.error('Twitter login failed:', error)

                addToast('error', 'Unable to login with Twitter.')
                navigate('/', { replace: true })
            } finally {
                cleanup()
                toast.dismiss()
            }
        }

        void handleCallback()
    }, [navigate])

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 99999,
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <BeatLoader
                color='#111111'
                size={10}
                margin={3}
                speedMultiplier={1}
            />
        </div>
    )
}
